import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Languages, Star, Users, Zap, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TranslationAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [serviceStatus, setServiceStatus] = useState('checking'); // 'checking', 'ready', 'warming'

  useEffect(() => {
    const checkServiceStatus = async () => {
      try {
        await TranslationAPI.checkHealth();
        setServiceStatus('ready');
      } catch (error) {
        setServiceStatus('warming');
      }
    };

    // Check status after a brief delay
    const timer = setTimeout(checkServiceStatus, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Helper function to get display name for mobile-friendly welcome message
  const getDisplayName = () => {
    if (!user?.name) return 'User';
    
    let displayName = 'User';
    
    // If the "name" is actually an email address, extract the part before @
    if (user.name.includes('@')) {
      const emailPrefix = user.name.split('@')[0];
      // Take first part if it has dots/underscores (like "just.zwennicker")
      displayName = emailPrefix.split('.')[0] || emailPrefix.split('_')[0] || 'User';
    } else {
      // If it's a proper name, take the first word
      displayName = user.name.split(' ')[0] || 'User';
    }
    
    // Capitalize the first letter
    return displayName.charAt(0).toUpperCase() + displayName.slice(1).toLowerCase();
  };

  const ServiceStatusIndicator = () => {
    const statusConfig = {
      checking: {
        icon: Loader,
        text: 'Starting up translation service...',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200'
      },
      ready: {
        icon: CheckCircle,
        text: 'Translation service is ready',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
      },
      warming: {
        icon: AlertCircle,
        text: 'Service warming up - translations may be slower initially',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-200'
      }
    };

    const config = statusConfig[serviceStatus];
    const IconComponent = config.icon;

    return (
      <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-3 max-w-2xl mx-auto mb-4`}>
        <div className="flex items-center justify-center space-x-2">
          <IconComponent className={`h-4 w-4 ${config.textColor} ${serviceStatus === 'checking' ? 'animate-spin' : ''}`} />
          <p className={`text-sm ${config.textColor} font-medium`}>
            {config.text}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-6 relative">
        <div className="absolute -top-8 right-0 bg-gray-100 border border-gray-300 rounded-full px-3 py-1 text-xs text-gray-600 font-medium whitespace-nowrap">
          ✨ {t('home.betaNotice')}
        </div>
        
        <div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {isAuthenticated ? `${t('home.welcomeBack')}, ${getDisplayName()}!` : t('home.title').split('Sranslate™')[0]} <span className="text-primary-600">Sranslate™</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('home.subtitle')}
          </p>
        </div>
        
        <ServiceStatusIndicator />

        {isAuthenticated ? (
          <Link
            to="/translate"
            className="inline-flex items-center space-x-2 btn-primary text-lg px-8 py-4"
          >
            <Languages size={24} />
            <span>Start Translating</span>
            <ArrowRight size={20} />
          </Link>
        ) : (
          <div className="space-y-4">
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 btn-primary text-lg px-8 py-4"
            >
              <Languages size={24} />
              <span>{t('home.signInButton')}</span>
              <ArrowRight size={20} />
            </Link>
            <p className="text-sm text-gray-500">
              {t('home.signInDescription')}
            </p>
          </div>
        )}
      </div>

      {/* How it Works */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
          {t('home.howItWorks.title')}
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
              1
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('home.howItWorks.step1.title')}
            </h3>
            <p className="text-gray-600">
              {t('home.howItWorks.step1.description')}
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
              2
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('home.howItWorks.step2.title')}
            </h3>
            <p className="text-gray-600">
              {t('home.howItWorks.step2.description')}
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
              3
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('home.howItWorks.step3.title')}
            </h3>
            <p className="text-gray-600">
              {t('home.howItWorks.step3.description')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;